import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Sparkle, X } from '@phosphor-icons/react';

// What's New entries — newest first. Add a new record when you bump APP_VERSION.
const WHATS_NEW = [
  {
    version: '3.9.0',
    date: 'Aug 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Mapping screen upgrade: low-confidence (red/yellow) columns now have a "See more values" expander — pull 20 real values from deep in the file to verify a mapping without opening Excel.',
      'New 8,000-contact Salesforce-style practice database for large-scale training runs (Salesforce → HubSpot and any other destination).',
      'Training license seeded (MC-GR-EVE1-2026) so migration trainees can download to any destination CRM up to 100,000 contacts.',
    ],
  },
  {
    version: '3.8.0',
    date: 'Aug 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Concierge is now a flat ADD-ON on every MigrationCrunch tier: +$500 (Contacts Only / Starter Pro), +$750 (Growth AI), +$1,000 (Enterprise AI) — included free with Unlimited AI. No per-record overage, ever.',
      'New page: /migrate/concierge walks customers through exactly what happens on a Concierge call — step by step — before they pay.',
      'You now get an instant email alert the moment a Concierge booking is paid, with phone, call window, CRM and size included.',
    ],
  },
  {
    version: '3.7.0',
    date: 'Aug 2026',
    title: "What's new in SalesCrunch",
    items: [
      'MigrationCrunch Concierge tier is live: $750 done-for-you migration (includes 7,500 records, then 10¢/record) with a phone + call-window intake before Stripe.',
      'Every Concierge booking lands in your CRM automatically — tagged concierge-migration with phone, call window, CRM and size in the notes.',
    ],
  },
  {
    version: '3.6.0',
    date: 'Aug 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Checkout Flow Sweep: a robot walked all 15 money paths (every MigrationCrunch tier, both subscriptions, credit packs, lead-gen gates) and verified each Stripe page on camera — 15 of 15 passed.',
      'The Anti-Salesforce Papers grew to eight essays — new: "The Bug Only a Robot Could See", the true story of our invisible checkout field.',
      'VideoCrunch now markets QA-on-camera: the same robot that films tutorials walks your checkout and hands you the recording. Interested visitors can raise a hand right on the page.',
    ],
  },
  {
    version: '3.5.1',
    date: 'Aug 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Emergency stop: every in-progress bulk send now has a red "Stop sending" button — remaining emails are cancelled on the spot.',
      'Double-send protection: the CRM now refuses to queue a second bulk send while one is still running, so a retry can never duplicate a whole batch.',
    ],
  },
  {
    version: '3.5.0',
    date: 'Aug 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Big bulk sends no longer time out: emails now go out in the background with a live "Sent 45 of 270…" progress bar — send to hundreds in one go.',
      'Print & Merge can now target a Tag directly — use the new "Filter by tag" dropdown above the contact list and everyone with that tag is pre-selected.',
      'The "Send via" picker (your Gmail vs. the SalesCrunch domain) now always shows on Print & Merge and in Crunchy\'s bulk-email drawer — with a pointer to connect Gmail if you haven\'t yet.',
    ],
  },
  {
    version: '3.4.0',
    date: 'Aug 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Crunchy went full-service: templates ("save that as a template called…"), Groups, deals ("mark the Hartman deal won"), and your schedule ("push today\'s tasks to Monday") — all by prompt.',
      'He can now also delete contacts and merge duplicates — always behind a red confirmation card showing exactly what happens before anything does.',
    ],
  },
  {
    version: '3.3.0',
    date: 'Aug 2026',
    title: "What's new in SalesCrunch",
    items: [
      'You can now SEE Crunchy working: a live status line shows exactly what he\'s doing ("Searching your CRM…", "Preparing the updates…") for the whole run — no more wondering if he\'s stuck.',
      'Crunchy also stopped guessing whether pasted contacts are new — he checks your CRM before making that call.',
    ],
  },
  {
    version: '3.2.0',
    date: 'Jun 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Bulk edit is here: tell Crunchy "change all my leads to prospect" — he shows the count, you confirm, done. Or check contacts in the Contact List and hit the new Bulk Edit button.',
      'Safety by design: only status and tags can be bulk-edited — notes, emails and phones can never be mass-overwritten.',
      'New contacts now start with a BLANK ID/Status (no more everything-is-a-lead). Pick the status when you actually know it.',
    ],
  },
  {
    version: '3.1.0',
    date: 'Jun 2026',
    title: "What's new in SalesCrunch",
    items: [
      'The ID/Status droplist is yours now — open it and hit "✎ Edit list…" to add your own statuses (Partner, Affiliate, Hot Lead…), rename or remove the classics. Pure ACT! 4.0.',
      'Your custom statuses flow everywhere: the Contact List filter, Lookups, exports — and Crunchy knows your list, so "mark Karen as a Partner" just works.',
      'Every batch you Apply now gets auto-tagged (updated-YYYY-MM-DD / added-YYYY-MM-DD) — ask Crunchy to "show me the contacts updated today" anytime.',
    ],
  },
  {
    version: '3.0.0',
    date: 'Jun 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Crunchy got a memory. Tell him "remember: my pitches always mention the free migration preview" and he\'ll know it in every conversation, forever. Say "forget that" to erase.',
      'He also remembers what he\'s DONE for you — bookings, contact batches, drafted emails — across all conversations. Ask "what have you done for me lately?"',
      'Crunchy now checks the CRM before creating contacts: people who already exist get enriched instead of duplicated — automatically.',
    ],
  },
  {
    version: '2.99.0',
    date: 'Jun 2026',
    title: "What's new in SalesCrunch",
    items: [
      'One-sentence scheduling: tell Crunchy "set a call for Aug 5 at 10am and brief me at 9:20" — he books the call AND a linked brief reminder in one shot.',
      "Today's Alarms now shows a gold View Brief button on Crunchy brief reminders — WHO / HISTORY / OPENER, right in the alarm window.",
      'Fixed: activities Crunchy books for you now show up in Today\'s Alarms and the Task List.',
    ],
  },
  {
    version: '2.97.0',
    date: 'Jun 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Crunchy sends one-on-one emails — say "send Karen the pitch in her notes" and Crunchy pulls the copy straight from the contact\'s Notes into a Compose window for your approval.',
      'Or let Crunchy write it — "draft a follow-up to Bob based on our history" and he writes a short, specific email from the contact\'s actual call and meeting record.',
      'Same safety rules as always: nothing sends until YOU click Send — via your connected Gmail or the SalesCrunch domain.',
    ],
  },
  {
    version: '2.16.0',
    date: 'Feb 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Marketing Kit PDFs — every doc in /kit now downloads as a styled PDF or opens a print-friendly view (great for trade shows).',
      'MigrationCrunch covers 12 destination CRMs — Salesforce, HubSpot, Pipedrive, Zoho, Insightly, Copper, Capsule, Less Annoying CRM, Freshsales, Keap, monday.com, plus generic CSV.',
      'Pipedrive/Copper/monday.com exports now correctly combine first + last name into the single "Name" column those CRMs require.',
    ],
  },
  {
    version: '2.15.0',
    date: 'Feb 2026',
    title: "What's new in SalesCrunch",
    items: [
      'Reusable Drop-down Lists — define "Lead Sources" once, point any user field at it. Update once, applies everywhere.',
      'Unlimited custom fields — no longer capped at 10. Add or remove fields directly in Settings.',
      'Outlook .pst import — directly import contacts from any Outlook PST file (no more CSV middleman).',
      'Workspace Backup now includes Drop-down Lists for true one-click setup cloning.',
      '10 user fields + 5 types + inline rename on the contact card (✎ pencil).',
    ],
  },
];

export default function WhatsNewToast() {
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/version');
        if (cancelled) return;
        const v = data.version;
        const seen = localStorage.getItem('whatsnew_seen_version');
        if (seen === v) return;
        const matching = WHATS_NEW.find(e => e.version === v) || WHATS_NEW[0];
        if (matching) setEntry({ ...matching, version: v });
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const close = () => {
    if (entry) localStorage.setItem('whatsnew_seen_version', entry.version);
    setEntry(null);
  };

  if (!entry) return null;

  return (
    <div
      className="fixed bottom-4 left-4 z-[60] max-w-sm bg-white border-2 border-[#0B5394] shadow-xl rounded-sm overflow-hidden"
      data-testid="whats-new-toast"
    >
      <div className="bg-gradient-to-r from-[#0B5394] to-[#073763] text-white px-3 py-2 flex items-center gap-2">
        <Sparkle size={15} weight="fill" className="text-[#F39C12]" />
        <span className="text-[12px] font-bold flex-1">{entry.title}</span>
        <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-sm">v{entry.version}</span>
        <button onClick={close} className="text-white/80 hover:text-white" aria-label="Close" data-testid="whats-new-close">
          <X size={14} weight="bold" />
        </button>
      </div>
      <div className="p-3">
        <ul className="space-y-1.5 text-[12px] text-[#333] list-disc pl-4">
          {entry.items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
        <div className="flex justify-end mt-3">
          <button
            onClick={close}
            className="text-[11px] px-3 py-1 bg-[#0B5394] text-white hover:bg-[#073763] rounded-sm font-medium"
            data-testid="whats-new-got-it"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
